import React, { useEffect, useState, RefObject, useMemo } from 'react'
import Box from '@mui/material/Box'
import { styled, useTheme } from '@mui/material/styles'
import IconButton from '@mui/material/IconButton'
import { Tabs } from './MainLayout'
import { useApplicationSuiteContext } from '../contexts/ApplicationSuiteProvider'
import HolidayVillageIcon from '@mui/icons-material/HolidayVillage'
import {
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  Badge,
} from '@mui/material'
import { collection, getCountFromServer, limit, orderBy, query, where } from 'firebase/firestore'
import { useCollectionData } from 'react-firebase-hooks/firestore'
import { firebase_firestore, firebase_realtime } from '../common/firebaseConfig'
import { Help, NotificationAdd } from '@mui/icons-material'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import { onValue, ref } from 'firebase/database'
import AssessmentIcon from '@mui/icons-material/Assessment'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import { StrategyTutorialRef } from '../components/tour/StrategyTutorial'
import { TRADING_HOUSE_STORAGE_KEY } from '../common/utils/tradingHouseBranding'

interface SideNavigationProps {
  tutorialRef: RefObject<StrategyTutorialRef>
}

function SideNavigation({ tutorialRef }: SideNavigationProps) {
  const theme = useTheme()
  const [percentage, setPercentage] = useState<number>(0)
  const listenerRef: any = React.useRef(null)

  const {
    setActiveTab,
    activeTab,
    tradingHouse,
    canToggleTradingHouse,
    setTradingHouse,
    usermeta,
    setIsCreateAlertModalOpen,
    canViewShowcaseApp,
    canViewPatternBuilderApp,
    canViewAlertApp,
    canCreateAlerts,
  } = useApplicationSuiteContext()

  const [hoveredIndex, setHoveredIndex] = useState<any>(null)
  const [hoveringTradingHouse, setHoveringTradingHouse] = useState(false)
  const [isTradingHouseSelectOpen, setIsTradingHouseSelectOpen] = useState(false)
  const [showCreateAlertIcon, setShowCreateAlertIcon] = useState(false)
  const [hoveringCreateAlertIcon, setHoveringCreateAlertIcon] = useState(false)
  const [hoveringTutorialIcon, setHoveringTutorialIcon] = useState(false)

  const canStartTutorial = useMemo(
    () =>
      activeTab.tab === Tabs.Alerts ||
      activeTab.tab === Tabs.Showcase ||
      activeTab.tab === Tabs.PatternBuilder,
    [activeTab.tab]
  )
  const activeTabName = useMemo(() => {
    if (activeTab.tab === Tabs.Alerts) {
      return 'Alerts'
    } else if (activeTab.tab === Tabs.Showcase) {
      return 'Backtests'
    } else if (activeTab.tab === Tabs.PatternBuilder) {
      return 'Patterns'
    } else {
      return 'GreenChart'
    }
  }, [activeTab.tab])

  const [backtests] = useCollectionData(
    query(
      collection(firebase_firestore, `usermeta/${usermeta.gcid}/backtests`),
      where('backtestSettings.tradingHouse', '==', tradingHouse),
      where('status', 'in', ['running']),
      orderBy('startTime', 'desc'),
      limit(1)
    )
  )

  // Query for new messages (only emailsInvited)
  const [newMessages] = useCollectionData(
    query(
      collection(firebase_firestore, `shareables`),
      where('emailsInvited', 'array-contains', usermeta.email)
    )
  )

  useEffect(() => {
    getCountFromServer(
      query(collection(firebase_firestore, `tradingPlans`), where('user.gcid', '==', usermeta.gcid))
    ).then((snapshot: any) =>
      setShowCreateAlertIcon(snapshot.data().count === 0 && canCreateAlerts)
    )
  }, [usermeta.gcid, canCreateAlerts])

  useEffect(() => {
    if (backtests && backtests.length > 0) {
      const backtest = backtests[0]
      let backtestDBRef = null

      const subStatus = backtest.subStatus

      let basePercentage = 0
      let rangePercentage = 50

      if (subStatus === 'signals') {
        backtestDBRef = ref(
          firebase_realtime,
          `usermeta/${usermeta.gcid}/backtests/${backtest.id}/findingSignalTasks`
        )
        basePercentage = 0
      } else if (subStatus === 'analyze') {
        backtestDBRef = ref(
          firebase_realtime,
          `usermeta/${usermeta.gcid}/backtests/${backtest.id}/analyzingRatioTasks`
        )
        basePercentage = 50
      }

      if (backtestDBRef) {
        listenerRef.current = onValue(backtestDBRef, (snapshot) => {
          const data = snapshot.val()
          if (data && data.total > 0) {
            const taskPercentage = ((data.total - data.remaining) / data.total) * 100
            const normalizedPercentage = basePercentage + (taskPercentage * rangePercentage) / 100
            setPercentage(normalizedPercentage)
          } else {
            setPercentage(basePercentage)
          }
        })
      } else {
        setPercentage(basePercentage)
      }
    } else {
      setPercentage(0)
    }

    return () => {
      if (listenerRef.current) {
        listenerRef.current()
        listenerRef.current = null
      }
    }
  }, [backtests, usermeta.gcid])

  function goToApplication(tab: Tabs) {
    setActiveTab({ tab: tab })
  }

  const handleTradingHouseChange = (e: { target: { value: any } }) => {
    const newValue = e.target.value
    localStorage.setItem(TRADING_HOUSE_STORAGE_KEY, newValue)
    setTradingHouse(newValue)
    setHoveringTradingHouse(false)
    setIsTradingHouseSelectOpen(false)
  }

  const handleTradingHouseMouseLeave = () => {
    // Don't close the menu if the select dropdown is open
    if (!isTradingHouseSelectOpen) {
      setHoveringTradingHouse(false)
    }
  }

  // Start the tutorial
  const startTutorial = () => {
    if (tutorialRef.current) {
      if (canStartTutorial) {
        tutorialRef.current.startTour()
      }
    }
  }

  const applicationOptions = [
    {
      icon: <AssessmentIcon fontSize={'large'} />,
      label: 'Backtests',
      description: 'Analyze trade results',
      tab: Tabs.Showcase,
      canView: canViewShowcaseApp,
    },
    {
      icon: <AccountTreeIcon fontSize={'large'} />,
      label: 'Pattern Builder',
      description: 'Define your trade entry',
      tab: Tabs.PatternBuilder,
      canView: canViewPatternBuilderApp,
    },
    {
      icon: <NotificationsActiveIcon fontSize={'large'} />,
      label: `Alerts`,
      description: 'Notified on trade entry',
      tab: Tabs.Alerts,
      canView: canViewAlertApp,
    },
    {
      icon: <SmartToyOutlinedIcon fontSize={'large'} />,
      label: 'AI Sessions',
      description: 'AI generated strategies',
      tab: Tabs.AgentSessions,
      canView: !!usermeta?.isAdmin,
    },
  ]

  return (
    <Box
      className="sideNavigation"
      py={2}
      sx={{
        backgroundColor: theme.palette.background.custom2,
        borderRadius: '8px 0 0 8px',
      }}
    >
      <Box height={'100%'} display={'flex'}>
        <Box display={'flex'} flexDirection={'column'} justifyContent={'space-between'} p={1}>
          <Box display={'flex'} flexDirection={'column'} alignItems={'center'} gap={1}>
            {applicationOptions
              .filter((option) => option.canView)
              .map((option, index) => (
                <Box
                  key={index}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  display={'flex'}
                  alignItems={'center'}
                  position="relative"
                  onClick={() => goToApplication(option.tab)}
                  sx={{
                    cursor: 'pointer',
                  }}
                >
                  <IconButton
                    className={`side-nav-icon ${option.tab === activeTab.tab ? 'active' : ''}`}
                    sx={{
                      '& svg': {
                        color: option.tab === activeTab.tab ? 'inherit' : 'action.disabled',
                      },
                    }}
                  >
                    {option.icon}
                  </IconButton>

                  {hoveredIndex === index && (
                    <Box
                      position="absolute"
                      display="flex"
                      alignItems="center"
                      justifyContent={'center'}
                      bgcolor={'rgba(56, 59, 66, 0.85)'}
                      height={62}
                      width={170}
                      left={'-4px'}
                      px={2}
                      pr={1}
                      pl={9}
                      borderRadius={2}
                      boxShadow={'0 8px 32px rgba(0, 0, 0, 0.3)'}
                      zIndex={100}
                      flexDirection={'column'}
                      sx={{
                        backdropFilter: 'blur(12px)',
                        border: '1px solid rgba(255, 255, 255, 0.06)',
                      }}
                    >
                      <Typography variant="body1" color="textPrimary">
                        {option.label}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {option.description}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}
          </Box>

          <Box display={'flex'} flexDirection={'column'} alignItems={'center'} gap={1}>
            {showCreateAlertIcon && canViewAlertApp && (
              <Box
                display={'flex'}
                alignItems={'center'}
                position="relative"
                onMouseEnter={() => setHoveringCreateAlertIcon(true)}
                onMouseLeave={() => setHoveringCreateAlertIcon(false)}
                onClick={() => setIsCreateAlertModalOpen(true)}
                sx={{
                  cursor: 'pointer',
                }}
              >
                <IconButton className={'side-nav-icon'} color={'success'}>
                  <NotificationAdd fontSize="medium" />
                </IconButton>

                {hoveringCreateAlertIcon && (
                  <Box
                    position="absolute"
                    display="flex"
                    flexDirection={'column'}
                    alignItems="center"
                    justifyContent={'center'}
                    bgcolor={'rgba(56, 59, 66, 0.85)'}
                    height={62}
                    sx={{
                      backdropFilter: 'blur(12px)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                    }}
                    width={130}
                    left={'-4px'}
                    px={2}
                    pr={1}
                    pl={9}
                    borderRadius={2}
                    boxShadow={3}
                    zIndex={101}
                  >
                    <Typography variant="body1" color="textPrimary" textAlign={'center'}>
                      Setup free Alert
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {backtests && backtests.length > 0 && (
              <Box
                onClick={() => goToApplication(Tabs.Showcase)}
                position="relative"
                display="inline-flex"
                sx={{ cursor: 'pointer' }}
              >
                <Tooltip title={`Backtest ${Math.round(percentage)}% complete`}>
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    {/* Background circle for the outline */}
                    <CircularProgress
                      variant="determinate"
                      size={40}
                      thickness={3.5}
                      sx={{
                        color: 'transparent',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        zIndex: 1,
                        '& .MuiCircularProgress-circle': {
                          strokeWidth: '3.5',
                          stroke: theme.palette.action.disabled,
                        },
                      }}
                      value={100}
                    />
                    {/* Main progress circle */}
                    <CircularProgress
                      variant="determinate"
                      value={percentage}
                      size={40}
                      thickness={3.5}
                      sx={{
                        color: theme.palette.info.main,
                        '& .MuiCircularProgress-circle': {
                          strokeWidth: '3.5',
                        },
                      }}
                    />
                    <Box
                      sx={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: 'absolute',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <IconButton size="small" sx={{ color: 'action.active' }}>
                        <AssessmentIcon fontSize={'small'} />
                      </IconButton>
                    </Box>
                  </Box>
                </Tooltip>
              </Box>
            )}

            <IconButton
              className={`side-nav-icon ${Tabs.Profile === activeTab.tab ? 'active' : ''}`}
              onClick={() => goToApplication(Tabs.Profile)}
              sx={{
                '& svg': {
                  color: Tabs.Profile === activeTab.tab ? 'inherit' : 'action.disabled',
                },
              }}
            >
              <Badge
                badgeContent={newMessages && newMessages.length > 0 ? newMessages.length : 0}
                color="error"
                overlap="circular"
                sx={{
                  '& .MuiBadge-badge': {
                    width: 16,
                    height: 16,
                    minWidth: 16,
                  },
                }}
              >
                <AccountCircleIcon fontSize="medium" />
              </Badge>
            </IconButton>

            {canStartTutorial && (
              <Box
                display={'flex'}
                alignItems={'center'}
                position="relative"
                onMouseEnter={() => setHoveringTutorialIcon(true)}
                onMouseLeave={() => setHoveringTutorialIcon(false)}
                onClick={startTutorial}
                sx={{
                  cursor: 'pointer',
                }}
              >
                <IconButton
                  className={'side-nav-icon'}
                  sx={{ '& svg': { color: 'action.disabled' } }}
                >
                  <Help fontSize="medium" />
                </IconButton>

                {hoveringTutorialIcon && (
                  <Box
                    position="absolute"
                    display="flex"
                    flexDirection={'column'}
                    alignItems="center"
                    justifyContent={'center'}
                    bgcolor={'rgba(56, 59, 66, 0.85)'}
                    height={62}
                    sx={{
                      backdropFilter: 'blur(12px)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                    }}
                    width={150}
                    left={'-4px'}
                    px={2}
                    pr={1}
                    pl={9}
                    borderRadius={2}
                    boxShadow={3}
                    zIndex={101}
                  >
                    <Typography variant="body1" color="textPrimary" textAlign={'center'}>
                      Start Tutorial
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Learn about {activeTabName}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {canToggleTradingHouse && (
              <Box
                display={'flex'}
                alignItems={'center'}
                position="relative"
                onMouseEnter={() => setHoveringTradingHouse(true)}
                onMouseLeave={handleTradingHouseMouseLeave}
              >
                <IconButton
                  className={'side-nav-icon'}
                  sx={{ '& svg': { color: 'action.disabled' } }}
                >
                  <HolidayVillageIcon fontSize="medium" />
                </IconButton>

                {hoveringTradingHouse && (
                  <Box
                    position="absolute"
                    display="flex"
                    flexDirection={'column'}
                    alignItems="center"
                    justifyContent={'center'}
                    bgcolor={'rgba(56, 59, 66, 0.85)'}
                    height={62}
                    sx={{
                      backdropFilter: 'blur(12px)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                    }}
                    width={150}
                    left={'-4px'}
                    px={2}
                    pr={1}
                    pl={9}
                    borderRadius={2}
                    boxShadow={3}
                    zIndex={101}
                  >
                    <StyledFormControl variant="outlined" size="small">
                      <InputLabel id="trading-house-label">Trading House</InputLabel>
                      <Select
                        labelId="trading-house-label"
                        id="trading-house-select"
                        value={tradingHouse}
                        onChange={handleTradingHouseChange}
                        onOpen={() => setIsTradingHouseSelectOpen(true)}
                        onClose={() => setIsTradingHouseSelectOpen(false)}
                        label={'Trading House'}
                      >
                        <MenuItem value="greenchart">GreenChart</MenuItem>
                        <MenuItem value="gtf">DTU</MenuItem>
                      </Select>
                    </StyledFormControl>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  width: '100%',
  cursor: 'pointer',
  '.MuiOutlinedInput-root': {
    backgroundColor: theme.palette.background.custom4,
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.23)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.5)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '.MuiInputBase-input': {
      color: theme.palette.text.primary,
    },
    '.MuiSvgIcon-root': {
      color: theme.palette.action.active,
    },
  },
  '.MuiInputLabel-root': {
    color: theme.palette.text.secondary,
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
  },
  '.MuiSelect-select': {
    '&:focus': {
      backgroundColor: 'transparent',
    },
    paddingRight: theme.spacing(4),
  },
  '.MuiPaper-root.MuiMenu-paper': {
    backgroundColor: theme.palette.background.custom4,
    color: theme.palette.text.primary,
  },
  '.MuiMenuItem-root': {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}))

export default SideNavigation
